/**
 * LandingChatWidget — floating bottom-right chatbot for the marketing
 * landing page. Powered by GPT-5.4 via /api/public/landing-chat (no auth).
 *
 * UX rules:
 *  - Session persists to localStorage so multi-turn context flows across
 *    the visitor's browsing (never expires until they clear storage).
 *  - Suggested-question chips shown on first open (dropped once the user
 *    types their own message).
 *  - Enter to send. Shift+Enter for newline.
 *  - Auto-scrolls to newest message.
 *  - Optimistic UI: user's message appears instantly with a "typing…" placeholder
 *    on the bot side.
 *  - Rate-limit errors surface a friendly toast (429 → "slow down" copy).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, Send, X, Sparkles, Loader2, Gift, Mail, Copy, CheckCircle2, ArrowRight, Headphones, User } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STORAGE_KEY = "legacy:landing-chat";

// Small ID gen — no external dep. Sessions live in localStorage so the
// bot remembers the visitor across page reloads.
const genId = () =>
  "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

export default function LandingChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);       // {role, text, promptEmail?, trialDays?, leadResult?}
  const [greeting, setGreeting] = useState("");
  const [chips, setChips] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Restore or create session
  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    } catch {}
    if (stored?.sessionId) {
      setSessionId(stored.sessionId);
      setMessages(stored.messages || []);
      setLeadCaptured(!!stored.leadCaptured);
    } else {
      const sid = genId();
      setSessionId(sid);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId: sid, messages: [] }));
    }
  }, []);

  // Persist on every message change
  useEffect(() => {
    if (!sessionId) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      sessionId, messages, leadCaptured,
    }));
  }, [sessionId, messages, leadCaptured]);

  // Load suggestions the first time the widget opens
  useEffect(() => {
    if (!open || chips.length) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/landing-chat/suggestions`);
        setGreeting(data.greeting);
        setChips(data.chips || []);
      } catch { /* fallback UX still works */ }
    })();
  }, [open, chips.length]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const send = async (text) => {
    const clean = (text || "").trim();
    if (!clean || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: clean }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/public/landing-chat`, {
        session_id: sessionId,
        message: clean,
      });
      setMessages((m) => [...m, {
        role: "bot",
        text: data.reply,
        promptEmail: !!data.prompt_email && !leadCaptured,
        offerRep: !!data.offer_rep_handoff,
        trialDays: data.trial_days || 30,
      }]);
      if (!open) setUnread(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
      setMessages((m) => [...m, { role: "bot", text: msg, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async (email, name = "") => {
    try {
      const { data } = await axios.post(`${API}/public/landing-chat/lead`, {
        session_id: sessionId,
        email,
        name,
      });
      setLeadCaptured(true);
      // Replace all promptEmail flags on prior bot messages so form disappears
      setMessages((m) => [
        ...m.map((msg) => msg.role === "bot" && msg.promptEmail
          ? { ...msg, promptEmail: false }
          : msg),
        { role: "bot", text: data.message, leadResult: data },
      ]);
      toast.success("30-day Premium trial unlocked!");
      return data;
    } catch (e) {
      const msg = e.response?.data?.detail || "Couldn't save your email. Please try again.";
      toast.error(msg);
      throw new Error(msg);
    }
  };

  const openChat = () => {
    setOpen(true);
    setUnread(false);
  };
  const closeChat = () => setOpen(false);

  const clearHistory = () => {
    const sid = genId();
    setSessionId(sid);
    setMessages([]);
    setLeadCaptured(false);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId: sid, messages: [] }));
  };

  return (
    <>
      {/* Floating bubble — visible when panel closed */}
      {!open && (
        <button
          onClick={openChat}
          className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-40 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-[var(--ld-royal)] text-white shadow-2xl shadow-[var(--ld-royal)]/40 hover:shadow-[var(--ld-royal)]/60 hover:-translate-y-0.5 transition-all duration-300"
          data-testid="landing-chat-open"
          aria-label="Open chat"
        >
          <div className="relative">
            <MessageSquare size={18} strokeWidth={2} />
            {unread && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            )}
          </div>
          <span className="text-sm font-semibold hidden sm:inline">Ask about Legacy IO</span>
          <span className="text-sm font-semibold sm:hidden">Ask</span>
          <Sparkles size={12} className="text-yellow-300 opacity-80 group-hover:rotate-12 transition-transform" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed inset-x-4 bottom-4 md:inset-auto md:bottom-8 md:right-8 md:w-[400px] z-50 flex flex-col rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
          style={{
            maxHeight: "min(640px, calc(100vh - 2rem))",
            background: "var(--ld-surface, #0f0f11)",
          }}
          data-testid="landing-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-[var(--ld-royal)]/30 via-transparent to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[var(--ld-royal)] flex items-center justify-center relative">
                <Sparkles size={14} className="text-white" />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[var(--ld-surface,#0f0f11)]" />
              </div>
              <div>
                <div className="text-sm font-heading font-bold text-white">Legacy IO Assistant</div>
                <div className="text-[10px] text-slate-400">Built by detailers · online now</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="text-slate-500 hover:text-slate-300 text-[10px] px-2 py-1 rounded hover:bg-white/5"
                  data-testid="landing-chat-clear"
                >
                  New chat
                </button>
              )}
              <button
                onClick={closeChat}
                className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-white/5"
                data-testid="landing-chat-close"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3"
            style={{ minHeight: "260px" }}
            data-testid="landing-chat-messages"
          >
            {/* Greeting bubble — always shown when no messages yet */}
            {messages.length === 0 && (
              <div className="flex justify-start" data-testid="landing-chat-greeting">
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/5 border border-white/5 px-3.5 py-2.5 text-sm text-slate-200 leading-relaxed">
                  {greeting || "Hey! I'm the Legacy Detailing IO assistant. Ask me anything about the app."}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "rounded-tr-sm bg-[var(--ld-royal)] text-white"
                      : m.error
                      ? "rounded-tl-sm bg-rose-500/10 border border-rose-500/30 text-rose-200"
                      : "rounded-tl-sm bg-white/5 border border-white/5 text-slate-200"
                  }`}
                  data-testid={`landing-chat-msg-${m.role}-${i}`}
                >
                  {m.text}
                </div>
                {/* Inline lead capture form under the bot's message that
                    triggered the 30-day trial offer */}
                {m.role === "bot" && m.promptEmail && !leadCaptured && (
                  <LeadCaptureCard
                    trialDays={m.trialDays || 30}
                    onSubmit={submitLead}
                  />
                )}
                {/* Success card once the lead is captured */}
                {m.role === "bot" && m.leadResult && (
                  <LeadSuccessCard result={m.leadResult} />
                )}
                {/* Rep hand-off offer card */}
                {m.role === "bot" && m.offerRep && (
                  <RepHandoffCard
                    sessionId={sessionId}
                    onAccepted={(result) => {
                      setMessages((mm) => [
                        ...mm.map((x) => x === m ? { ...x, offerRep: false } : x),
                        { role: "bot", text: result.message, repResult: result },
                      ]);
                    }}
                  />
                )}
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="landing-chat-typing">
                <div className="rounded-2xl rounded-tl-sm bg-white/5 border border-white/5 px-3.5 py-2.5 text-sm text-slate-400 flex items-center gap-2">
                  <span className="typing-dot" />
                  <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                  <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
                </div>
              </div>
            )}
          </div>

          {/* Suggested chips (first-time only) */}
          {messages.length === 0 && chips.length > 0 && (
            <div className="p-3 pt-0 border-t border-white/5" data-testid="landing-chat-chips">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                Popular questions
              </div>
              <div className="flex flex-wrap gap-1.5">
                {chips.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => send(c)}
                    className="text-[11px] px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-[var(--ld-royal)]/25 border border-white/10 hover:border-[var(--ld-royal-hover)]/40 text-slate-300 hover:text-white transition-colors"
                    data-testid={`landing-chat-chip-${i}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-white/10 bg-black/20">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(input);
                  }
                }}
                placeholder="Ask about pricing, features, or how it compares…"
                className="flex-1 resize-none bg-white/5 border border-white/10 focus:border-[var(--ld-royal-hover)]/40 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none max-h-32"
                rows={1}
                maxLength={800}
                data-testid="landing-chat-input"
              />
              <button
                onClick={() => send(input)}
                disabled={!input.trim() || sending}
                className="w-9 h-9 rounded-lg bg-[var(--ld-royal)] hover:bg-[var(--ld-royal-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors flex-shrink-0"
                data-testid="landing-chat-send"
                aria-label="Send message"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </div>
            <div className="mt-1.5 text-[9px] text-slate-500 text-center">
              AI-powered · Answers may not be perfect. Start the free trial to see it live.
            </div>
          </div>
        </div>
      )}

      {/* Tiny typing-dot CSS — scoped to this component */}
      <style>{`
        .typing-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: rgba(255,255,255,0.5);
          display: inline-block;
          animation: typing-bounce 1s infinite ease-in-out;
        }
        @keyframes typing-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </>
  );
}


/* ────────── Inline lead capture card (rendered under 3rd bot message) ────────── */
function LeadCaptureCard({ trialDays, onSubmit }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(email.trim(), name.trim());
    } catch { /* toast already fired */ }
    finally { setSubmitting(false); }
  };

  return (
    <form
      onSubmit={handle}
      className="mt-2 max-w-[85%] w-full rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent p-3.5 space-y-2"
      data-testid="landing-chat-lead-form"
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Gift size={14} className="text-emerald-400" />
        <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold">
          {trialDays}-DAY PREMIUM · FREE
        </div>
      </div>
      <div className="text-xs text-slate-300 leading-relaxed">
        Drop your email and I'll shoot you an unlock code. No credit card. Cancel any time before day {trialDays} and you owe zero.
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name (optional)"
        className="w-full bg-white/5 border border-white/10 focus:border-emerald-500/40 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none"
        data-testid="landing-chat-lead-name"
      />
      <div className="flex gap-1.5">
        <div className="flex-1 flex items-center gap-1.5 bg-white/5 border border-white/10 focus-within:border-emerald-500/40 rounded-md px-2.5">
          <Mail size={13} className="text-slate-500 flex-shrink-0" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@detailingco.com"
            className="w-full bg-transparent py-2 text-sm text-white placeholder:text-slate-500 outline-none"
            data-testid="landing-chat-lead-email"
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="px-3 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-white text-xs font-bold flex items-center gap-1 transition-colors flex-shrink-0"
          data-testid="landing-chat-lead-submit"
        >
          {submitting ? <Loader2 size={12} className="animate-spin" /> : <Gift size={12} />}
          {submitting ? "…" : "Claim"}
        </button>
      </div>
      <div className="text-[9px] text-slate-500">
        We'll never spam. Unsubscribe with one click. See our privacy page.
      </div>
    </form>
  );
}


/* ────────── Success card shown after lead captured ────────── */
function LeadSuccessCard({ result }) {
  const [copied, setCopied] = useState(false);
  const code = result.promo_code || "PREMIUM30";
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — long-press to copy manually."); }
  };
  return (
    <div
      className="mt-2 max-w-[85%] rounded-2xl border border-emerald-500/50 bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent p-3.5"
      data-testid="landing-chat-lead-success"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <CheckCircle2 size={16} className="text-emerald-400" />
        <div className="text-sm font-heading font-bold text-white">
          {result.trial_days}-Day Premium unlocked!
        </div>
      </div>
      <div className="text-xs text-slate-300 mb-3 leading-relaxed">
        Your one-time promo code:
      </div>
      <button
        onClick={copyCode}
        className="w-full flex items-center justify-between gap-2 bg-black/40 border border-emerald-500/40 rounded-md px-3 py-2 mb-3 hover:bg-black/60 transition-colors"
        data-testid="landing-chat-lead-copy-code"
      >
        <span className="font-mono text-sm font-bold text-emerald-300 tabular-nums">
          {code}
        </span>
        {copied ? <CheckCircle2 size={13} className="text-emerald-400" /> : <Copy size={13} className="text-slate-400" />}
      </button>
      <a
        href={result.register_url || "/register"}
        className="w-full flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold py-2 rounded-md transition-colors"
        data-testid="landing-chat-lead-cta"
      >
        Start my free trial <ArrowRight size={13} />
      </a>
    </div>
  );
}


/* ────────── Rep Hand-off Card (chatbot offers to connect a certified specialist) ────────── */
function RepHandoffCard({ sessionId, onAccepted }) {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const connect = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/rep-market/handoff`, {
        session_id: sessionId,
        customer_email: email.trim(),
        customer_name: name.trim(),
        context: "Requested certified specialist from landing chatbot",
      });
      if (data.ok) {
        toast.success(`Matched with ${data.rep_name}!`);
        onAccepted(data);
      } else {
        toast.warning(data.message || "No specialists available right now.");
        onAccepted(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't connect right now.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={connect}
      className="mt-2 max-w-[85%] w-full rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 via-cyan-500/5 to-transparent p-3.5 space-y-2"
      data-testid="landing-chat-rep-handoff">
      <div className="flex items-center gap-1.5 mb-1">
        <Headphones size={14} className="text-cyan-300"/>
        <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-bold">
          TALK TO A CERTIFIED SPECIALIST
        </div>
      </div>
      <div className="text-xs text-slate-300 leading-relaxed">
        Want a real detailer-turned-specialist to walk you through your exact setup? We'll match you with one right now, free.
      </div>
      <input type="text" value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        className="w-full bg-white/5 border border-white/10 focus:border-cyan-500/40 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none"
        data-testid="landing-chat-rep-name"/>
      <div className="flex gap-1.5">
        <div className="flex-1 flex items-center gap-1.5 bg-white/5 border border-white/10 focus-within:border-cyan-500/40 rounded-md px-2.5">
          <Mail size={13} className="text-slate-500"/>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourshop.com"
            className="w-full bg-transparent py-2 text-sm text-white placeholder:text-slate-500 outline-none"
            data-testid="landing-chat-rep-email"/>
        </div>
        <button type="submit" disabled={busy || !email.trim()}
          className="px-3 py-2 rounded-md bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-white text-xs font-bold flex items-center gap-1"
          data-testid="landing-chat-rep-submit">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <User size={12}/>}
          {busy ? "…" : "Connect"}
        </button>
      </div>
    </form>
  );
}
